import type { FC } from 'react'
import { useState, useEffect } from 'react'

import { InfoBox, LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'
import EditIcon from 'mdi-react/EditIcon'
import Select from 'react-select'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useIsAdmin } from '@/hooks/access'
import useUser, { useProfile } from '@/hooks/user'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import { departmentResources } from '@/resources/departments'
import type { Employee } from '@/resources/employees'
import employeeResources from '@/resources/employees'
import { commonQueries } from '@/resources/queries'

import SelectedUser from '../SelectedUser'

import './step-user.scss'

const StepUser: FC<{
  employee: Employee | undefined
}> = ({ employee }) => {
  const [userType, setUserType] = useState<'employee' | 'location' | null>(null)

  const translate = useTranslation()
  const profile = useProfile()
  const user = useUser()
  const isCustomerAdmin = user?.role === 'customer_admin'
  const isAdmin = useIsAdmin()

  const shopCtx = useShopNewOrderContext()
  const { resetOrderData } = shopCtx
  const [, setCostObjectId] = shopCtx.costObjectId
  const [, setSelectedAddressId] = shopCtx.selectedAddressId
  const [, setActiveStepName] = shopCtx.activeStepName
  const [isCostObjectValid, setIsCostObjectValid] = shopCtx.isCostObjectValid

  useEffect(() => {
    setActiveStepName('USER')
  }, [setActiveStepName])

  const { data, isLoading } = useQuery(commonQueries.employees(), () =>
    employeeResources.getList(isAdmin)
  )

  const { data: departmentHead, isLoading: departmentHeadIsLoading } = useQuery(
    commonQueries.departmentHead(employee?.departmentId || 0),
    () => departmentResources.getDepartmentHead(employee?.departmentId || 0),
    {
      retry: (count, error: { response: Response }) => {
        if (error.response.status === 404) {
          return false
        }

        return count < 2
      },
    }
  )

  const missingEmail =
    employee &&
    !employee.isLocation &&
    !employee.email &&
    !departmentHeadIsLoading &&
    !departmentHead?.email
  const missingPhone =
    employee &&
    !employee.isLocation &&
    !employee.code &&
    !departmentHeadIsLoading &&
    !departmentHead?.phoneNumber
  const translationId =
    missingEmail || missingPhone
      ? `cost-object-missing${missingEmail ? '-email' : ''}${missingPhone ? '-phone' : ''}`
      : null

  useEffect(() => {
    setIsCostObjectValid(translationId === null)
  }, [setIsCostObjectValid, translationId])

  const userCostObjectId = data?.employees?.find(
    employee => employee.code === profile?.phoneNumber
  )?.id

  const selectMyself = () => {
    setUserType(null)
    setCostObjectId(userCostObjectId || null)
  }

  const reset = () => {
    setUserType(null)

    setCostObjectId(null)
    // When changing the user, all related selections should be wiped
    resetOrderData()
  }

  if (isLoading || departmentHeadIsLoading) return <LoaderCircle />
  if (!data) throw Error('Something went wrong while fetching user list!')
  const { userList, locationList } = data
  if (data?.employees?.length === 0) return <NoResults />

  const userOptions = userList.map(user => ({
    value: user.id,
    label: `${user.name ? `${user.name} - ` : ''}${
      user.departmentName ? `${user.departmentCode} ${user.departmentName} - ` : ''
    }${user.code}${user.employeeNo ? ` - ${user.employeeNo}` : ''}`,
  }))

  const filteredLocations = isCustomerAdmin
    ? locationList?.filter(location => location.customerId === user.customerId)
    : locationList
  const locationOptions =
    filteredLocations?.map(location => ({
      value: location.id,
      label: `${location.code} - ${location.name} - ${location.departmentCode} - ${location.departmentName}`,
      isLocation: true,
    })) || []

  const costObjectId = employee?.id
  const isForLocation = userType === 'location'
  const isUserSelectionAvailable = userList.length > 0
  const isLocationSelectionAvailable = locationList.length > 0
  const isOnlyUserSelectionAvailable = !isLocationSelectionAvailable && !userCostObjectId
  const isOnlyLocationSelectionAvailable = !isUserSelectionAvailable && !userCostObjectId

  if (employee) {
    return (
      <div className="step-user">
        <div className="selected-user-container">
          {!isCostObjectValid && translationId && (
            <InfoBox emphasis="warning" className="mb-2">
              <AlertIcon />
              <Translation id={translationId ?? ''} />
            </InfoBox>
          )}
          <SelectedUser employee={employee} isForLocation={isForLocation} />

          <Button onClick={reset} Icon={<EditIcon />}>
            <Translation id={isForLocation ? 'Change location' : 'Change user'} />
          </Button>
        </div>
      </div>
    )
  }

  if (!isUserSelectionAvailable && !isLocationSelectionAvailable)
    return (
      <NoResults>
        <Translation id="No valid users..." />
      </NoResults>
    )

  if (userType === 'employee' || isOnlyUserSelectionAvailable)
    return (
      <div className="step-user">
        <Select
          id="department-select"
          className="react-select"
          placeholder={translate('Select employee')}
          options={userOptions}
          onChange={(option: (typeof userOptions)[number]) => {
            setCostObjectId(option.value || null)
          }}
          value={userOptions.find(option => option.value === costObjectId)}
        />
      </div>
    )

  if (userType === 'location' || isOnlyLocationSelectionAvailable)
    return (
      <div className="step-user">
        <Select
          id="department-select"
          className="react-select"
          placeholder={translate('Select location')}
          options={locationOptions}
          onChange={(option: (typeof locationOptions)[number]) => {
            setCostObjectId(option?.value || null)
            const selectedLocationCostObject = data?.locationList.find(
              location => location.id === option?.value
            )
            if (selectedLocationCostObject?.customerAddressId) {
              setSelectedAddressId(selectedLocationCostObject.customerAddressId)
            }
          }}
          value={locationOptions.find(option => option.value === costObjectId)}
        />
      </div>
    )

  return (
    <div className="step-user">
      <div className="select-user-type">
        <h3>
          <Translation id="Who is this order for?" />
        </h3>
        <div className="mx-auto flex max-w-[50rem] flex-wrap justify-center">
          {userCostObjectId && (
            <Button className="m-4 h-28 w-60 flex-[1_1_10rem] px-5 text-2xl" onClick={selectMyself}>
              <Translation id="Myself" className="text-[38px]" />
            </Button>
          )}

          {isUserSelectionAvailable && (
            <Button
              className="m-4 h-28 w-60 flex-[1_1_10rem] px-5 text-2xl"
              onClick={() => setUserType('employee')}
            >
              <Translation id={userCostObjectId ? 'Other' : 'Employee'} className="text-[38px]" />
            </Button>
          )}

          {!userCostObjectId && isLocationSelectionAvailable && (
            <Button
              className="m-4 h-28 w-60 flex-[1_1_10rem] px-5 text-2xl"
              onClick={() => setUserType('location')}
            >
              <Translation id="Location" className="text-[38px]" />
            </Button>
          )}
        </div>

        {userCostObjectId && isLocationSelectionAvailable && (
          <div className="mx-auto flex max-w-[30rem] flex-wrap justify-center">
            <Button
              className="m-4 h-28 w-60 flex-[1_1_10rem] px-5 text-2xl"
              onClick={() => setUserType('location')}
            >
              <Translation id="Location" className="text-[38px]" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default StepUser
